import type { TOptions } from 'i18next';
import type { SnackbarKey } from 'notistack';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { is403Error, is404Error, isNetworkError } from '~/shared/error-code/error-code';
import type { TranslationKey } from '~/shared/internationalization/translation';

type SnackBars = {
    readonly showErrorMessageSnackbar: (translationKey: TranslationKey, options?: TOptions) => SnackbarKey;
    readonly showErrorSnackbar: (error: unknown) => SnackbarKey;
    readonly showInfoSnackbar: (translationKey: TranslationKey) => SnackbarKey;
    readonly showSuccessSnackbar: (translationKey: TranslationKey, options?: TOptions) => SnackbarKey;
};

const clickToDismissStyle = { cursor: 'pointer' } as const;

export const useSnackbars = (): SnackBars => {
    const { translate } = useContext(ApplicationContext);
    const { enqueueSnackbar } = useSnackbar();

    const showErrorSnackbar = (error: unknown): SnackbarKey => {
        const getMessage = (): string => {
            if (is403Error(error)) {
                return translate('errors.status.403.message');
            }
            if (is404Error(error)) {
                return translate('errors.status.404.message');
            }
            if (isNetworkError(error)) {
                return translate('errors.network.message');
            }

            return translate('errors.common.message');
        };

        const key = enqueueSnackbar(getMessage(), {
            SnackbarProps: {
                onClick: () => closeSnackbar(key),
                style: clickToDismissStyle,
            },
            variant: 'error',
        });

        return key;
    };

    const showSuccessSnackbar = (translationKey: TranslationKey, options?: TOptions): SnackbarKey => {
        const key = enqueueSnackbar(translate(translationKey, options), {
            SnackbarProps: {
                onClick: () => closeSnackbar(key),
                style: clickToDismissStyle,
            },
            variant: 'success',
        });

        return key;
    };

    const showErrorMessageSnackbar = (translationKey: TranslationKey, options?: TOptions): SnackbarKey => {
        const key = enqueueSnackbar(translate(translationKey, options), {
            SnackbarProps: {
                onClick: () => closeSnackbar(key),
                style: clickToDismissStyle,
            },
            variant: 'error',
        });

        return key;
    };

    const showInfoSnackbar = (translationKey: TranslationKey): SnackbarKey => {
        const key = enqueueSnackbar(translate(translationKey), {
            SnackbarProps: {
                onClick: () => closeSnackbar(key),
                style: clickToDismissStyle,
            },
            variant: 'info',
        });

        return key;
    };

    return {
        showErrorMessageSnackbar,
        showErrorSnackbar,
        showInfoSnackbar,
        showSuccessSnackbar,
    };
};
